import React, { useEffect } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { useField } from 'react-final-form';

export enum ExaminationReason {
    A1 = 101,
    A2 = 102,
    A3 = 103,

    B1 = 201,
    B2 = 202,
    B3 = 203,

    C1 = 301,
    C2 = 302,
    C3 = 303,
    C4 = 304,
    C5 = 305,
}

export const ExaminationReasonMap: Record<number, string> = {
    [ExaminationReason.A1]: 'A1 借讀生',
    [ExaminationReason.A2]: 'A2 轉（入）學生',
    [ExaminationReason.A3]: 'A3 在籍不在學',

    [ExaminationReason.B1]: 'B1 行動無法配合檢查',
    [ExaminationReason.B2]: 'B2 特殊生無法配合檢查',
    [ExaminationReason.B3]: 'B3 特教生領有身心障礙證明，無法配合測量',

    [ExaminationReason.C1]: 'C1 嚴重傷病無法測量',
    [ExaminationReason.C2]: 'C2 中輟生／未到校上課',
    [ExaminationReason.C3]: 'C3 家長或學生本人現場拒絕受檢',
    [ExaminationReason.C4]: 'C4 健檢同意書已勾選不同意受檢',
    [ExaminationReason.C5]: 'C5 其它',
};

type Props = {
    type?: 'wh' | 'sight' | 'physical' | 'tee';
    enabled?: boolean;
    examinationProperty?: string;
    examinationStateProperty?: string;
};

export const ExaminationReasonField: React.FC<Props> = ({
    type,
    enabled,
    examinationProperty,
    examinationStateProperty,
}) => {
    const isTee = type === 'tee';

    const reasonProperty =
        examinationProperty ??
        (isTee ? 'teeExamination' : 'examination');

    const reasonStateProperty =
        examinationStateProperty ??
        (isTee ? 'teeExaminationState' : 'examinationState');

    const heightField = useField<number | string | null>('height', {
        subscription: {
            value: true,
        },
    });

    const weightField = useField<number | string | null>('weight', {
        subscription: {
            value: true,
        },
    });

    const sight0RField = useField<number | string | null>('sight0R', {
        subscription: {
            value: true,
        },
    });

    const examinationField = useField<number | null>(reasonProperty, {
        subscription: {
            value: true,
        },
    });

    const examinationStateField = useField<string | null>(
        reasonStateProperty,
        {
            subscription: {
                value: true,
            },
        }
    );

    const examination = examinationField.input.value;

    const examinationState =
        examinationStateField.input.value ?? '';

    const fieldIsUnexamined =
        type === 'wh'
            ? Number(heightField.input.value) === -9 ||
            Number(weightField.input.value) === -9
            : type === 'sight'
                ? Number(sight0RField.input.value) === -9
                : false;

    const isUnexamined =
        enabled !== undefined
            ? enabled
            : fieldIsUnexamined;

    const needDescription = [
        ExaminationReason.C1,
        ExaminationReason.C3,
        ExaminationReason.C5,
    ].includes(Number(examination) as ExaminationReason);

    useEffect(() => {
        if (!isUnexamined) {
            if (
                examination !== null &&
                examination !== undefined
            ) {
                examinationField.input.onChange(null);
            }

            if (examinationState) {
                examinationStateField.input.onChange(null);
            }
        }
    }, [isUnexamined]);

    const handleReasonChange = (reason: ExaminationReason) => {
        examinationField.input.onChange(reason);

        if (
            ![
                ExaminationReason.C1,
                ExaminationReason.C3,
                ExaminationReason.C5,
            ].includes(reason)
        ) {
            examinationStateField.input.onChange(null);
        }
    };

    if (!isUnexamined) {
        return (
            <span className="text-muted">                
            </span>
        );
    }

    const reasonGroups = [
        {
            label: 'A. 特殊學籍者',
            options: [
                ExaminationReason.A1,
                ExaminationReason.A2,
                ExaminationReason.A3,
            ],
        },
        {
            label: 'B. 身心障礙無法受檢者',
            options: [
                ExaminationReason.B1,
                ExaminationReason.B2,
                ExaminationReason.B3,
            ],
        },
        {
            label: 'C. 非上述身分之未受檢者',
            options: [
                ExaminationReason.C1,
                ExaminationReason.C2,
                ExaminationReason.C3,
                ExaminationReason.C4,
                ExaminationReason.C5,
            ],
        },
    ];

    const selectedReasonCode = examination
        ? ExaminationReasonMap[Number(examination)]?.split(' ')[0]
        : '';

    return (
        <div style={{ width: '100%' }}>
            <Dropdown>
                <Dropdown.Toggle
                    as="span"
                    className="btn btn-outline-secondary border-0 px-2 py-1 m-0"
                    style={{
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {selectedReasonCode || (
                        <span className="feather icon-search" />
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                    style={{
                        width: '360px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                    }}
                >
                    {reasonGroups.map((group, groupIndex) => (
                        <React.Fragment key={group.label}>
                            {groupIndex > 0 && <Dropdown.Divider />}

                            <div className="px-3 py-1 font-weight-bold text-muted">
                                {group.label}
                            </div>

                            {group.options.map(reason => (
                                <Dropdown.Item
                                    key={reason}
                                    active={Number(examination) === reason}
                                    onSelect={() => handleReasonChange(reason)}
                                    style={{ whiteSpace: 'normal' }}
                                >
                                    {ExaminationReasonMap[reason]}
                                </Dropdown.Item>
                            ))}
                        </React.Fragment>
                    ))}
                </Dropdown.Menu>
            </Dropdown>

            {needDescription && (
                <Form.Control
                    type="text"
                    size="sm"
                    className="mt-2"
                    value={examinationState}
                    placeholder={
                        Number(examination) === ExaminationReason.C1
                            ? '診斷名稱或理由'
                            : Number(examination) === ExaminationReason.C3
                                ? '原因'
                                : '說明'
                    }
                    onChange={(event) => {
                        examinationStateField.input.onChange(event.target.value);
                    }}
                />
            )}
        </div>
    );
};

export const getExaminationReasonLabel = (
    examination?: unknown,
    examinationState?: unknown
): string => {
    if (
        examination === null ||
        examination === undefined
    ) {
        return '';
    }

    const reasonValue = Number(examination);

    if (Number.isNaN(reasonValue)) {
        return '';
    }

    const reason =
        ExaminationReasonMap[reasonValue] ?? '';

    if (!reason) {
        return '';
    }

    const state =
        typeof examinationState === 'string'
            ? examinationState
            : '';

    const needDescription = [
        ExaminationReason.C1,
        ExaminationReason.C3,
        ExaminationReason.C5,
    ].includes(reasonValue as ExaminationReason);

    if (needDescription && state) {
        return `${reason}：${state}`;
    }

    return reason;
};

export default ExaminationReasonField;